import { useEffect, useRef, useState } from 'react';
import { FileUpload } from 'primereact/fileupload';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Messages } from 'primereact/messages';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Tag } from 'primereact/tag';
import postClienteDocumento from '../../service/postClienteDocumento';
import getClienteDocumentos from '../../service/getClienteDocumentos';
import getClienteDocumentoDownload from '../../service/getClienteDocumentoDownload';
import deleteClienteDocumento from '../../service/deleteClienteDocumento';

interface Props {
  idCliente: string;
}

export default function ClienteDocumentos({ idCliente }: Props) {
  const msgs = useRef<Messages>(null);
  const fileUploadRef = useRef<FileUpload>(null);
  const [documentos, setDocumentos] = useState<any[]>([]);
  const [descricao, setDescricao] = useState('');
  const [arquivoBase64, setArquivoBase64] = useState('');
  const [nomeArquivo, setNomeArquivo] = useState('');
  const [uploading, setUploading] = useState(false);
  const [tipoDocumento, setTipoDocumento] = useState<string>('OUTRO');
  const [dataInicioVigencia, setDataInicioVigencia] = useState<Date | null>(null);
  const [duracaoMeses, setDuracaoMeses] = useState<number | null>(12);

  const tipoOptions = [
    { label: 'Contrato', value: 'CONTRATO' },
    { label: 'Proposta', value: 'PROPOSTA' },
    { label: 'Outro', value: 'OUTRO' },
  ];

  useEffect(() => {
    if (idCliente) {
      carregarDocumentos();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCliente]);

  const carregarDocumentos = () => {
    getClienteDocumentos(idCliente)
      .then(setDocumentos)
      .catch(() => mostrarErro('Erro ao carregar documentos'));
  };

  const onSelectArquivo = (e: { files: File[] }) => {
    const file = e.files[0];
    if (!file) return;
    setNomeArquivo(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      if (ev.target?.result) {
        const base64 = btoa(
          new Uint8Array(ev.target.result as ArrayBuffer).reduce(
            (data, byte) => data + String.fromCharCode(byte),
            ''
          )
        );
        setArquivoBase64(base64);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const salvarDocumento = () => {
    if (!arquivoBase64 && tipoDocumento !== 'CONTRATO') {
      mostrarAviso('Selecione um arquivo antes de salvar.');
      return;
    }
    if (tipoDocumento === 'CONTRATO' && (!dataInicioVigencia || !duracaoMeses)) {
      mostrarAviso('Informe o início da vigência e a duração do contrato.');
      return;
    }
    setUploading(true);
    postClienteDocumento(idCliente, {
      descricao,
      nomeArquivo,
      arquivo: arquivoBase64,
      tipoDocumento,
      dataInicioVigencia: dataInicioVigencia,
      duracaoMeses: tipoDocumento === 'CONTRATO' ? duracaoMeses : null,
    })
      .then(() => {
        mostrarSucesso('Documento salvo com sucesso.');
        setDescricao('');
        setArquivoBase64('');
        setNomeArquivo('');
        setTipoDocumento('OUTRO');
        setDataInicioVigencia(null);
        setDuracaoMeses(12);
        fileUploadRef.current?.clear();
        carregarDocumentos();
      })
      .catch(() => mostrarErro('Erro ao salvar documento.'))
      .finally(() => setUploading(false));
  };

  const baixarDocumento = (doc: any) => {
    getClienteDocumentoDownload(doc.id)
      .then((data) => {
        if (!data.arquivo) return;
        const bytes = Uint8Array.from(atob(data.arquivo), (c) => c.charCodeAt(0));
        const blob = new Blob([bytes]);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.nomeArquivo || 'documento';
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => mostrarErro('Erro ao baixar documento.'));
  };

  const confirmarExclusao = (doc: any) => {
    confirmDialog({
      message: `Deseja excluir o documento "${doc.nomeArquivo}"?`,
      header: 'Confirmar Exclusão',
      icon: 'pi pi-trash',
      acceptLabel: 'Excluir',
      rejectLabel: 'Cancelar',
      acceptClassName: 'p-button-danger',
      accept: () => {
        deleteClienteDocumento(doc.id)
          .then(() => {
            mostrarSucesso('Documento excluído.');
            carregarDocumentos();
          })
          .catch(() => mostrarErro('Erro ao excluir documento.'));
      }
    });
  };

  const mostrarSucesso = (msg: string) => {
    msgs.current?.clear();
    msgs.current?.show([{ sticky: false, life: 5000, severity: 'success', summary: 'Sucesso', detail: msg, closable: false }]);
  };

  const mostrarErro = (msg: string) => {
    msgs.current?.clear();
    msgs.current?.show([{ sticky: false, life: 8000, severity: 'error', summary: 'Erro', detail: msg, closable: false }]);
  };

  const mostrarAviso = (msg: string) => {
    msgs.current?.clear();
    msgs.current?.show([{ sticky: false, life: 5000, severity: 'warn', summary: 'Atenção', detail: msg, closable: false }]);
  };

  const acoesTemplate = (doc: any) => (
    <div style={{ display: 'flex', gap: '0.5rem' }}>
      <Button
        type="button"
        icon="pi pi-download"
        tooltip="Baixar"
        tooltipOptions={{ position: 'top' }}
        text
        style={{ color: '#183462' }}
        onClick={() => baixarDocumento(doc)}
      />
      <Button
        type="button"
        icon="pi pi-trash"
        tooltip="Excluir"
        tooltipOptions={{ position: 'top' }}
        text
        severity="danger"
        onClick={() => confirmarExclusao(doc)}
      />
    </div>
  );

  const dataTemplate = (doc: any) => {
    if (!doc.dataInclusao) return '';
    return new Date(doc.dataInclusao).toLocaleDateString('pt-BR');
  };

  const tipoLabel: Record<string, string> = { CONTRATO: 'Contrato', PROPOSTA: 'Proposta', OUTRO: 'Outro' };
  const tipoTemplate = (doc: any) => {
    const t = doc.tipoDocumento || 'OUTRO';
    return <Tag value={tipoLabel[t] || t} severity={t === 'CONTRATO' ? 'info' : t === 'PROPOSTA' ? 'warning' : undefined} />;
  };

  const vencimentoTemplate = (doc: any) => {
    if (!doc.dataVencimento) return '—';
    return new Date(doc.dataVencimento).toLocaleDateString('pt-BR');
  };

  return (
    <div>
      <ConfirmDialog />
      <Messages ref={msgs} />

      <h4 className="font-semibold mb-3 border-bottom pb-2" style={{ color: '#4b5563', fontSize: '1.25rem' }}>
        Adicionar Documento
      </h4>

      <div className="grid mb-3">
        <div className="col-12 md:col-4">
          <label className="font-bold block mb-2">Tipo</label>
          <Dropdown
            style={{ width: '100%' }}
            value={tipoDocumento}
            options={tipoOptions}
            onChange={(e) => setTipoDocumento(e.value)}
          />
        </div>
        <div className="col-12 md:col-8">
          <label className="font-bold block mb-2">Descrição</label>
          <InputText
            style={{ width: '100%' }}
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            placeholder="Descrição do documento (opcional)"
          />
        </div>
      </div>

      {tipoDocumento === 'CONTRATO' && (
        <div className="grid mb-3">
          <div className="col-12 md:col-4">
            <label className="font-bold block mb-2">Início da vigência <span className="text-red-500">*</span></label>
            <Calendar
              style={{ width: '100%' }}
              value={dataInicioVigencia}
              onChange={(e) => setDataInicioVigencia(e.value as Date)}
              dateFormat="dd/mm/yy"
              showIcon
            />
          </div>
          <div className="col-12 md:col-4">
            <label className="font-bold block mb-2">Duração <span className="text-red-500">*</span></label>
            <Dropdown
              style={{ width: '100%' }}
              value={duracaoMeses}
              options={[{ label: '12 meses', value: 12 }, { label: '24 meses', value: 24 }]}
              onChange={(e) => setDuracaoMeses(e.value)}
            />
          </div>
          <div className="col-12 md:col-4">
            <label className="font-bold block mb-2">Vencimento</label>
            <InputText
              style={{ width: '100%' }}
              disabled
              value={
                dataInicioVigencia && duracaoMeses
                  ? new Date(new Date(dataInicioVigencia).setMonth(new Date(dataInicioVigencia).getMonth() + duracaoMeses)).toLocaleDateString('pt-BR')
                  : ''
              }
              placeholder="Calculado automaticamente"
            />
          </div>
        </div>
      )}

      <div className="grid mb-3 align-items-end">
        <div className="col">
          <label className="font-bold block mb-2">Arquivo <span className="text-red-500">*</span></label>
          <FileUpload
            ref={fileUploadRef}
            mode="basic"
            chooseLabel={nomeArquivo ? nomeArquivo : 'Selecionar arquivo'}
            auto={false}
            customUpload
            onSelect={onSelectArquivo}
          />
        </div>
        <div className="col-fixed" style={{ paddingBottom: '0.25rem' }}>
          <Button
            type="button"
            label="Salvar Documento"
            icon="pi pi-save"
            loading={uploading}
            onClick={salvarDocumento}
            style={{ backgroundColor: '#183462', borderColor: '#183462' }}
          />
        </div>
      </div>

      <h4 className="font-semibold mb-3 border-bottom pb-2 mt-4" style={{ color: '#4b5563', fontSize: '1.25rem' }}>
        Documentos Anexados
      </h4>

      <DataTable value={documentos} emptyMessage="Nenhum documento cadastrado." stripedRows>
        <Column header="Tipo" body={tipoTemplate} style={{ width: '8rem' }} />
        <Column field="nomeArquivo" header="Arquivo" />
        <Column field="descricao" header="Descrição" />
        <Column header="Vencimento" body={vencimentoTemplate} style={{ width: '9rem' }} />
        <Column field="usuarioInclusao" header="Incluído por" />
        <Column header="Data Inclusão" body={dataTemplate} />
        <Column header="Ações" body={acoesTemplate} style={{ width: '8rem' }} />
      </DataTable>
    </div>
  );
}
